import api from "./api.service";
import {
  Token,
  NomeCompleto,
  ItemsMenu,
  Status,
  IdUsuario,
  PermissoesChaves,
  Email,
  // TipoCadastroPlanTrib,
} from "../utils/localStorage.util";

/**
 * TXM-214 — gestão de usuários/grupos: permissões granulares por chave.
 * Chamadas HTTP desse escopo devem usar /gerenciamento/* (não /grupos-usuarios na API).
 * Espelho formal Jira: /api/grupos e /api/permissoes/arvore (integrações), não o app PTRIB.
 */

export const AuthServices = {
  login: (loginResponse) => {
    Token.setToken(loginResponse.token);
    ItemsMenu.setItemsMenu(JSON.stringify(loginResponse.menusPermitidos));
    NomeCompleto.setNomeCompleto(loginResponse.usuario.nomeCompleto);
    if (loginResponse?.usuario?.idUsuario != null) {
      IdUsuario.setUserId(loginResponse.usuario.idUsuario);
    }
  },
  logout: () => {
    Token.removeToken();
    NomeCompleto.removeNomeCompleto();
    ItemsMenu.removeItemsMenu();
    IdUsuario.removeUserId();
    // Status.removeStatus();
    // Email.removeEmail();
    // TipoCadastroPlanTrib.removeTipoCadastroPlanTrib();
  },
};

export function authenticationHandler(loginRequest) {
  return api.post("/usuarios/autenticar", loginRequest);
}

export function verificarTokenHandler(token) {
  return api.post("/usuarios/verificar-token", token);
}

/** Chaves de permissão do usuário logado (novo modelo — TXM-216). */
export function buscarMinhasPermissoes() {
  return api.get("/usuarios/minhas-permissoes");
}

export function alterarSenhaHandler(token, nova_senha) {
  const payload = { ...nova_senha, token: token != null ? String(token).trim() : "" };
  return api.put("/usuarios/redefinir-senha", payload);
}

export function enviarCodigoVerificacaoHandler(email) {
  return api.post("/usuarios/enviar-email-confirmacao", { email });
}

export function validarCodigoVerificacaoHandler(payload) {
  return api.post("/usuarios/validar-email-confirmacao", payload);
}

export function buscarComboPorCodigo(codigoCombo) {
  return api.get(`/configuracoes/combos/${codigoCombo}`);
}

// Novo fluxo: APIs sob /gerenciamento (permissões por chave)
export function buscarUsuarios() {
  return api.get(`/gerenciamento/usuarios`);
}

/** TXM-219: listagem paginada + stats (canônico GET /gerenciamento/usuarios/catalogo; espelho GET /api/usuarios). */
export function listarUsuariosCatalogo(params = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.tipo) q.set("tipo", params.tipo);
  if (params.grupo != null && String(params.grupo).trim() !== "") q.set("grupo", String(params.grupo).trim());
  if (params.nivel_associacao_id != null && String(params.nivel_associacao_id).trim() !== "")
    q.set("nivel_associacao_id", String(params.nivel_associacao_id).trim());
  if (params.associado_id != null && String(params.associado_id).trim() !== "")
    q.set("associado_id", String(params.associado_id).trim());
  if (params.search != null && String(params.search).trim() !== "") q.set("search", String(params.search).trim());
  if (params.sort) q.set("sort", params.sort);
  if (params.order) q.set("order", params.order);
  q.set("page", String(params.page ?? 1));
  q.set("per_page", String(params.per_page ?? 25));
  const qs = q.toString();
  return api.get(`/gerenciamento/usuarios/catalogo${qs ? `?${qs}` : ""}`);
}

export function listarAssociadosFiltroUsuarios() {
  return api.get(`/gerenciamento/usuarios/associados-filtro`);
}

export function atualizarUsuario(usuario) {
  const idUsuario = usuario.idUsuario ?? usuario.id_usuario;
  const temGruposIdsExplicito =
    Object.prototype.hasOwnProperty.call(usuario, "gruposIds") && Array.isArray(usuario.gruposIds);
  let grupos;
  if (temGruposIdsExplicito) {
    grupos = [...new Set(usuario.gruposIds.map((g) => Number(g)).filter((n) => !Number.isNaN(n) && n > 0))];
  } else {
    const fromIds =
      Array.isArray(usuario.gruposSelecionados) && usuario.gruposSelecionados.length > 0
        ? usuario.gruposSelecionados
        : usuario.idGrupoUsuario != null && String(usuario.idGrupoUsuario).trim() !== ""
          ? [usuario.idGrupoUsuario]
          : [];
    grupos = [...new Set(fromIds.map((g) => Number(g)).filter((n) => !Number.isNaN(n) && n > 0))];
  }
  const { gruposIds: _g1, gruposSelecionados: _g2, ...usuarioRest } = usuario;
  usuarioRest.idGrupoUsuario = grupos.length > 0 ? grupos[0] : null;
  usuarioRest.gruposSelecionados = grupos.length > 0 ? grupos : [];
  /** Inclui na raiz quando o payload traz a chave (tela de usuários sempre envia; `[]` limpa diretas no BD). */
  let permissoesExtrasPayload;
  if (Object.prototype.hasOwnProperty.call(usuario, "permissoesExtras")) {
    permissoesExtrasPayload = Array.isArray(usuario.permissoesExtras) ? usuario.permissoesExtras : [];
  }
  return api.put(`/gerenciamento/usuarios/${idUsuario}`, {
    usuario: usuarioRest,
    ...(temGruposIdsExplicito || grupos.length > 0 ? { grupos } : {}),
    ...(permissoesExtrasPayload !== undefined ? { permissoesExtras: permissoesExtrasPayload } : {}),
  });
}

/** Ativar/Desativar usuário — novo fluxo (§6.1). */
export function atualizarAtivacaoUsuario(idUsuario, ativo) {
  return api.put(`/gerenciamento/usuarios/${idUsuario}/ativacao`, {
    ativo: Boolean(ativo),
  });
}

/** Cadastro antigo (planejamento-tributario). Preferir criarUsuarioGerenciamento para novo fluxo. */
export function cadastrarUsuario(tipoCadastro, usuario) {
  return api.post(`/usuarios/planejamento-tributario/${tipoCadastro}`, {
    usuario,
  });
}

/** Criar usuário — novo fluxo (§6.1). POST /gerenciamento/usuarios. Grupo opcional; `grupos` pode ser []. */
export function criarUsuarioGerenciamento(usuario, grupos = []) {
  const gruposIds = Array.isArray(grupos)
    ? [...new Set(grupos.map((id) => Number(id)).filter((n) => !Number.isNaN(n) && n > 0))]
    : [];
  const idDoUsuario =
    usuario.idGrupoUsuario != null && !Number.isNaN(Number(usuario.idGrupoUsuario)) && Number(usuario.idGrupoUsuario) > 0
      ? Number(usuario.idGrupoUsuario)
      : gruposIds[0] ?? null;
  const permissoesExtrasRoot =
    Object.prototype.hasOwnProperty.call(usuario, "permissoesExtras") && Array.isArray(usuario.permissoesExtras)
      ? usuario.permissoesExtras
      : undefined;
  return api.post("/gerenciamento/usuarios", {
    usuario: {
      ...usuario,
      idGrupoUsuario: idDoUsuario,
    },
    grupos: gruposIds,
    ...(permissoesExtrasRoot !== undefined ? { permissoesExtras: permissoesExtrasRoot } : {}),
  });
}

export function buscarUsuariosPorTexto(texto) {
  return api.get(`/gerenciamento/usuarios/buscar-por-campos?textoDeBusca=${texto}`);
}

/** Detalhe completo do usuário (dadosComplementares, niveisAcesso, grupos N:N) — GET /gerenciamento/usuarios/:id */
export function getUsuarioDetalheGerenciamento(idUsuario) {
  return api.get(`/gerenciamento/usuarios/${idUsuario}`);
}

/** TXM-220: upload multipart — POST /api/usuarios/:id/upload-documento (tipo: contrato_social | cartao_cnpj) */
export function uploadDocumentoUsuarioTxm220(idUsuario, tipo, file) {
  const fd = new FormData();
  fd.append("tipo", tipo);
  fd.append("arquivo", file);
  return api.post(`/api/usuarios/${idUsuario}/upload-documento`, fd);
}

/** TXM-220: remove documento físico e limpa URL — DELETE /api/usuarios/:id/documentos/:tipo */
export function deleteDocumentoUsuarioTxm220(idUsuario, tipo) {
  return api.delete(`/api/usuarios/${idUsuario}/documentos/${tipo}`);
}

/** Lista de grupos para filtro e modal de usuário; exige *-users (não *-roles). */
export function buscarGruposUsuarios() {
  return api.get(`/gerenciamento/usuarios/grupos-opcoes`);
}

/**
 * TXM-217: lista paginada + stats.
 * Caminho canônico do app: GET /gerenciamento/grupos/catalogo (espelho em GET /api/grupos para contrato externo).
 */
export function buscarGruposCatalogo(params = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.nivel_acesso) q.set("nivel_acesso", params.nivel_acesso);
  if (params.search != null && String(params.search).trim() !== "")
    q.set("search", String(params.search).trim());
  q.set("page", String(params.page ?? 1));
  q.set("per_page", String(params.per_page ?? 20));
  const qs = q.toString();
  return api.get(`/gerenciamento/grupos/catalogo${qs ? `?${qs}` : ""}`);
}

export function duplicarGrupoUsuario(idGrupo) {
  return api.post(`/gerenciamento/grupos/${idGrupo}/duplicar`);
}

export function patchGrupoUsuarioStatus(idGrupo, ativo) {
  return api.patch(`/gerenciamento/grupos/${idGrupo}/status`, { ativo: Boolean(ativo) });
}

export function buscarNiveisAssociacaoGerenciamento() {
  return api.get(`/gerenciamento/niveis-associacao`);
}

/** Árvore para tela de Grupos (papéis) — exige chave *-roles. */
export function getPermissoesArvore() {
  return api.get(`/gerenciamento/permissoes/arvore`);
}

/** Árvore para modal de Usuários (permissões adicionais) — exige *-users. */
export function getPermissoesArvoreUsuario() {
  return api.get(`/gerenciamento/usuarios/permissoes/arvore`);
}

/** Detalhe do grupo para edição: { grupo, permissoes: string[] } — exige *-roles. */
export function getGrupoDetalhe(idGrupo) {
  return api.get(`/gerenciamento/grupos/${idGrupo}/detalhe`);
}

/** Só chaves de permissão do grupo — modal de usuário (herança); exige *-users. */
export function getGrupoPermissoesParaUsuario(idGrupo) {
  return api.get(`/gerenciamento/usuarios/grupos/${idGrupo}/permissoes`);
}

export function getPermissoes() {
  return api.get(`/gerenciamento/grupos/buscar-permissoes`);
}

export function excluirGrupoUsuario(idGrupo) {
  return api.delete(`/gerenciamento/grupos/${idGrupo}`);
}

/** TXM-218: valida nome único (case-insensitive). Body: { nome, idGrupo? } */
export function validarNomeGrupoGerenciamento(nome, idGrupo = null) {
  return api.post(`/gerenciamento/grupos/validar-nome`, {
    nome: String(nome || "").trim(),
    ...(idGrupo != null ? { idGrupo } : {}),
  });
}

/** TXM-218: preview de impacto ao salvar permissões no grupo */
export function previewAlteracaoPermissoesGrupo(idGrupo, permissoes = []) {
  return api.post(`/gerenciamento/grupos/${idGrupo}/preview-alteracao`, {
    permissoes: Array.isArray(permissoes) ? permissoes : [],
  });
}

export function editarGrupoUsuario(idGrupo, body) {
  return api.put(`/gerenciamento/grupos/${idGrupo}`, body);
}

export function inserirGrupoUsuario(body) {
  return api.post(`/gerenciamento/grupos/`, body);
}

/** Compat: mesmo que editarGrupoUsuario — PUT /gerenciamento/grupos/:id, body { grupo, permissoes } (chaves). TXM-214/218. */
export function atualizarfuncionalidadesGpPtrib(idGrupo, body) {
  return editarGrupoUsuario(idGrupo, body);
}

/** Compat: mesmo que getGrupoDetalhe — GET /gerenciamento/grupos/:id/detalhe (permissoes como chaves). */
export function buscarFuncionalidadesPtrib(idGrupo) {
  return getGrupoDetalhe(idGrupo);
}

/** Compat: alias de excluirGrupoUsuario. */
export function excluirGrupoUsuarioPtrib(idGrupo) {
  return excluirGrupoUsuario(idGrupo);
}

export function buscarGruposUsuariosPorTexto(textoBusca) {
  return api.get(`/gerenciamento/grupos/buscar-por-texto?textoBusca=${textoBusca}`);
}

export function buscarFuncionalidades() {
  return api.get(`/funcionalidades/`);
}

export function editarFuncionalidade(funcionalidade) {
  return api.put(`/funcionalidades/editar`, funcionalidade);
}

export function inserirFuncionalidade(funcionalidade) {
  return api.post(`/funcionalidades`, funcionalidade);
}

export function excluirFuncionalidade(idFuncionalidade) {
  return api.delete(`/funcionalidades/${idFuncionalidade}`);
}

export function buscarFuncionalidadesPorTexto(texto) {
  return api.get(`/funcionalidades/buscar-por-texto?textoBusca=${texto}`);
}

export function inserirFuncionalidadesExtraordiarias(idUsuario, body) {
  return api.post(`/gerenciamento/usuarios/atualizar-funcionalidades/${idUsuario}`, body);
}

/** Permissões do usuário (novo fluxo): retorna { permissoes: [ { id: chave, label } ] }. */
export function getPermissoesUsuario(idUsuario) {
  return api.get(`/gerenciamento/usuarios/buscar-funcionalidade-usuario/${idUsuario}`);
}

/** @deprecated Use getPermissoesUsuario. Mantido apenas para compatibilidade. */
export function buscarFuncionalidadesPorUsuario(idUsuario) {
  return api.get(`/gerenciamento/usuarios/buscar-funcionalidade-usuario/${idUsuario}`);
}

//Parâmetros
export function buscarTodosParametros() {
  return api.get(`/parametros/`);
}

export function inserirParametros(body) {
  return api.post(`/parametros/`, body);
}

export function excluirParametro(idParametro) {
  return api.delete(`/parametros/${idParametro}`);
}

export function editarParametro(idParametro, body) {
  return api.put(`/parametros/${idParametro}`, body);
}

export function buscarParametroPorTexto(texto) {
  return api.get(`/parametros/buscar-parametro-por-texto?texto=${texto}`);
}

export function buscarParametroPorChave(chave) {
  return api.get(`/parametros/buscar-parametro-por-chave/${chave}`);
}

//endpoint valida cpf e email
export function existeDuplicidade(tipo, texto) {
  return api.get(`/usuarios/consulta-duplicidade-base/${tipo}/${texto}`);
}

//endpoint de consulta cnpj por ID
export function consultaCnpjPorId(idUsuario) {
  return api.get(`/usuarios/consulta-cnpj-por-id/${idUsuario}`);
}

/** Consulta dados do CNPJ na Receita Federal via api-autenticacao (independente da api-empresas). */
export function consultarCNPJUsuario(cnpj) {
  const cnpjLimpo = String(cnpj).replace(/[^\d]/g, '');
  return api.get(`/usuarios/consultar-cnpj/${cnpjLimpo}`);
}

export function buscarCombosRelatorios() {
  return api.get(`/configuracoes/combos`);
}

export function buscarLoginPorEmail(email) {
  return api.get(`/usuarios/buscar-login-por-email?email=${email}`);
}

export function enviarTokenRedefinicaoDeSenha(body) {
  return api.post(`/usuarios/enviar-token-senha-tributario`, body);
}

export function buscarGrupoEconomicoPorSchema(schema) {
  return api.get(`/configuracoes/grupo-por-empresa/${schema}`);
}
